import {
  ReviewOffer,
  disagreementCount,
  reviewOffer,
  type AdversarialRun,
} from '../lib/adversarialReview.ts'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'
import { Tooltip } from './Tooltip.tsx'

/**
 * The super admin's way into the adversarial review (#541).
 *
 * One button, and everything else happens in the modal it opens. With nothing
 * run yet it starts a review; after that it opens the modal, which is where a
 * run is followed and where the choice between reading the result, walking it
 * and running again is made.
 *
 * It was a menu first. A menu is a small thing to aim at for three choices
 * that deserve a sentence each, and it had nowhere to show a run in progress.
 */
export interface AdversarialReviewControlProps {
  /** `undefined` while the first answer is on its way. */
  readonly run: AdversarialRun | null | undefined
  readonly starting: boolean
  /** The conferência is being analysed: a review now would be outdated on arrival. */
  readonly analysing: boolean
  /** Starts the first review. */
  readonly onRun: () => void
  /** Opens the modal on whatever the latest run is. */
  readonly onOpen: () => void
}

export function AdversarialReviewControl({
  run,
  starting,
  analysing,
  onRun,
  onOpen,
}: AdversarialReviewControlProps) {
  const offer = reviewOffer(run)

  // Nothing run yet: the button is the action.
  if (offer === ReviewOffer.None && !starting) {
    return (
      <Tooltip
        text={
          analysing
            ? 'Revisão adversarial — aguarde a análise da conferência terminar.'
            : 'Rodar revisão adversarial — um modelo mais forte lê a receita de novo e aponta onde a leitura, o pareamento ou os apontamentos estão errados. Leva alguns minutos.'
        }
      >
        <Button
          variant="secondary"
          tone="neutral"
          size="icon"
          leadingIcon={icons.secondOpinion}
          aria-label="Rodar revisão adversarial"
          disabled={analysing || run === undefined}
          onClick={onRun}
        />
      </Tooltip>
    )
  }

  const running = starting || offer === ReviewOffer.Running
  return (
    <Tooltip text={summary(offer, run, running)}>
      <Button
        variant="secondary"
        tone={offer === ReviewOffer.Ready && !running ? 'brand' : 'neutral'}
        size="icon"
        leadingIcon={running ? icons.busy : icons.secondOpinion}
        aria-label={running ? 'Acompanhar a revisão adversarial' : 'Revisão adversarial'}
        aria-haspopup="dialog"
        onClick={onOpen}
      />
    </Tooltip>
  )
}

/** What the latest run came to, in one sentence. */
function summary(
  offer: ReviewOffer,
  run: AdversarialRun | null | undefined,
  running: boolean,
): string {
  if (running) return 'Revisão adversarial em andamento — abrir para acompanhar.'
  if (offer === ReviewOffer.Failed) {
    return `A última revisão falhou: ${run?.error ?? 'sem motivo registrado'}.`
  }
  if (offer === ReviewOffer.Outdated) {
    return 'A conferência foi analisada de novo depois da última revisão, que ficou desatualizada.'
  }
  return `Revisão adversarial: ${disagreementCount(run?.disagreements.length ?? 0)}.`
}
